import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Fuel, Plus, X, Warehouse, CheckCircle2, XCircle, Truck, Image as ImageIcon, Send } from "lucide-react";

/**
 * Bowser Loadings — three source types, each with its own workflow:
 *  • Depot          — tank → bowser, admin creates + auto-approves in one step
 *  • Private point  — admin assigns to driver, driver submits empty+loaded weight
 *                     + slip photo, admin approves. Litres = net_kg / density.
 *  • Petrol pump    — admin assigns to driver, driver submits start+end reading
 *                     + slip photo, admin approves. Litres = end - start.
 *
 * Approval commits the litres to the bowser's `current_stock`. Rejection sends
 * the loading back to the driver (status = assigned) for resubmit.
 */

const STATUS_STYLES = {
  assigned: "bg-status-scheduled/10 text-status-scheduled border-status-scheduled/30",
  submitted: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  rejected: "bg-status-cancelled/10 text-status-cancelled border-status-cancelled/30",
  voided: "bg-muted text-muted-foreground border-muted-foreground/30",
};

const SOURCE_LABELS = {
  depot: "Depot",
  private_point: "Private Point",
  petrol_pump: "Petrol Pump",
};


export default function Loadings() {
  const qc = useQueryClient();
  const [depotOpen, setDepotOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const { data } = useQuery({
    queryKey: ["loadings"],
    queryFn: () => api.get("/loadings").then((r) => r.data.items || []),
    refetchInterval: 30000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["loadings"] });
    qc.invalidateQueries({ queryKey: ["dispatch-available"] });
  };

  const approveMut = useMutation({
    mutationFn: (id) => api.post(`/loadings/${id}/approve`),
    onSuccess: () => { toast.success("Loading approved · bowser stock topped up"); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Approve failed"),
  });
  const voidMut = useMutation({
    mutationFn: (id) => api.delete(`/loadings/${id}`),
    onSuccess: () => { toast.success("Loading voided — stock reversed"); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Void failed"),
  });

  return (
    <div className="space-y-6" data-testid="loadings-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight">Bowser Loadings</h1>
          <p className="text-sm text-muted-foreground">
            Depot self-refuel · driver-assigned refills at private points & petrol pumps · approval tops up bowser stock
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setDepotOpen(true)} data-testid="btn-new-depot-loading">
            <Warehouse size={16} className="mr-1" /> Depot loading
          </Button>
          <Button onClick={() => setAssignOpen(true)} data-testid="btn-assign-loading">
            <Plus size={16} className="mr-1" /> Assign to driver
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>All loadings</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data || []).length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No loadings yet. Depot for on-site tank refills; Assign for driver-run pickups.
            </div>
          )}
          {(data || []).map((l) => (
            <LoadingRow
              key={l.id}
              l={l}
              onApprove={() => approveMut.mutate(l.id)}
              onReject={() => setRejectFor(l)}
              onVoid={() => window.confirm(`Void ${l.code}? Stock will be reversed if it was approved.`) && voidMut.mutate(l.id)}
              approving={approveMut.isPending}
            />
          ))}
        </CardContent>
      </Card>

      <DepotLoadingDialog open={depotOpen} onClose={() => setDepotOpen(false)} onDone={invalidate} />
      <AssignLoadingDialog open={assignOpen} onClose={() => setAssignOpen(false)} onDone={invalidate} />
      <RejectDialog loading={rejectFor} onClose={() => setRejectFor(null)} onDone={invalidate} />
    </div>
  );
}


function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.voided;
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {status || "—"}
    </span>
  );
}


function LoadingRow({ l, onApprove, onReject, onVoid, approving }) {
  const src = l.source_type || "depot";
  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-border/60 p-4 md:grid-cols-12" data-testid={`loading-${l.code}`}>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Code · Source</div>
        <div className="font-mono font-bold">{l.code}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">{SOURCE_LABELS[src] || src}</div>
      </div>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Bowser</div>
        <div className="text-sm font-semibold">{l.bowser_code || "—"}</div>
        {src === "depot" ? (
          <div className="text-[11px] text-muted-foreground">from {l.tank_code || "—"}</div>
        ) : (
          <div className="text-[11px] text-muted-foreground">{l.vendor_name || "—"}</div>
        )}
      </div>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Driver</div>
        <div className="text-sm">{l.driver_name || "—"}</div>
      </div>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Litres</div>
        <div className="text-lg font-black text-primary">
          {l.litres_loaded ? Number(l.litres_loaded).toFixed(0) : "—"} L
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div>
        <div className="mt-0.5"><StatusPill status={l.status || "approved"} /></div>
        {l.rejected_reason && (
          <div className="mt-0.5 text-[11px] text-status-cancelled" title={l.rejected_reason}>
            Last reject: {l.rejected_reason.slice(0, 42)}{l.rejected_reason.length > 42 ? "…" : ""}
          </div>
        )}
      </div>
      <div className="md:col-span-2 flex items-center justify-end gap-2">
        {l.slip_photo_url && (
          <a href={l.slip_photo_url} target="_blank" rel="noreferrer" title="View slip photo" data-testid={`slip-${l.code}`}>
            <Button size="sm" variant="ghost"><ImageIcon size={14} /></Button>
          </a>
        )}
        {l.status === "submitted" && (
          <>
            <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-500/10"
                    onClick={onApprove} disabled={approving} data-testid={`btn-approve-${l.code}`} title="Approve">
              <CheckCircle2 size={16} />
            </Button>
            <Button size="sm" variant="ghost" className="text-status-cancelled hover:bg-status-cancelled/10"
                    onClick={onReject} data-testid={`btn-reject-${l.code}`} title="Reject">
              <XCircle size={16} />
            </Button>
          </>
        )}
        {l.status !== "voided" && (
          <Button size="sm" variant="ghost" onClick={onVoid} data-testid={`btn-void-${l.code}`} title="Void">
            <X size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}


function AssignLoadingDialog({ open, onClose, onDone }) {
  const { data: bowsers } = useQuery({
    queryKey: ["fleet-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data.items || r.data || []),
    enabled: open,
  });
  const { data: drivers } = useQuery({
    queryKey: ["fleet-drivers"], queryFn: () => api.get("/fleet/drivers").then((r) => r.data.items || r.data || []),
    enabled: open,
  });
  const { data: sources } = useQuery({
    queryKey: ["loading-sources"], queryFn: () => api.get("/loading-sources").then((r) => r.data.items || []),
    enabled: open,
  });
  const initial = {
    source_type: "private_point",
    bowser_id: "", driver_id: "",
    vendor_name: "", expected_litres: 0,
    density_kg_per_litre: 0.832,
    notes: "",
  };
  const [form, setForm] = useState(initial);
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mut = useMutation({
    mutationFn: () => api.post("/loadings/assign", form),
    onSuccess: () => {
      toast.success("Loading assigned · driver has been notified");
      setForm(initial);
      onDone?.();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Assign failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="assign-loading-dialog">
        <DialogHeader>
          <DialogTitle>Assign external loading</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Source type</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["private_point", "petrol_pump"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setVal("source_type", s)}
                  data-testid={`assign-source-${s}`}
                  className={`rounded-md border px-3 py-2 text-left text-sm ${form.source_type === s ? "border-primary bg-primary/10 font-bold" : "border-border/60 hover:border-border"}`}
                >
                  <div className="font-semibold">{SOURCE_LABELS[s]}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {s === "private_point" ? "Weighbridge · driver enters empty + loaded weight" : "Retail pump · driver enters start + end reading"}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Bowser</Label>
            <Select value={form.bowser_id} onValueChange={(v) => setVal("bowser_id", v)}>
              <SelectTrigger data-testid="assign-bowser"><SelectValue placeholder="Choose bowser" /></SelectTrigger>
              <SelectContent>
                {(bowsers || []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.code} · {(b.current_stock || 0).toFixed(0)} / {(b.capacity_litres || 0).toFixed(0)} L
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Driver</Label>
            <Select value={form.driver_id} onValueChange={(v) => setVal("driver_id", v)}>
              <SelectTrigger data-testid="assign-driver"><SelectValue placeholder="Choose driver" /></SelectTrigger>
              <SelectContent>
                {(drivers || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{form.source_type === "petrol_pump" ? "Pump / station" : "Supplier / weighbridge"}</Label>
            <Select
              value={form.vendor_name}
              onValueChange={(v) => {
                const src = (sources || []).find((s) => s.name === v);
                setForm((f) => ({
                  ...f,
                  vendor_name: v,
                  density_kg_per_litre:
                    f.source_type === "private_point" && src?.default_density_kg_per_litre
                      ? src.default_density_kg_per_litre
                      : f.density_kg_per_litre,
                }));
              }}
            >
              <SelectTrigger data-testid="assign-vendor"><SelectValue placeholder="Choose source" /></SelectTrigger>
              <SelectContent>
                {(sources || [])
                  .filter((s) => s.source_type === form.source_type)
                  .map((s) => (
                    <SelectItem key={s.id} value={s.name}>
                      {s.name}{s.address ? ` · ${s.address.slice(0, 32)}` : ""}
                    </SelectItem>
                  ))}
                {(sources || []).filter((s) => s.source_type === form.source_type).length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No saved {SOURCE_LABELS[form.source_type].toLowerCase()}s yet. Add one in <b>Loading Sources</b>.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Expected litres (optional)</Label>
            <Input
              type="number" step="0.1"
              value={form.expected_litres || ""}
              onChange={(e) => setVal("expected_litres", Number(e.target.value))}
              placeholder="e.g. 5000"
              data-testid="assign-expected"
            />
          </div>
          {form.source_type === "private_point" && (
            <div className="col-span-2">
              <Label>Density (kg/L) <span className="text-status-cancelled">*</span></Label>
              <Input
                type="number" step="0.001"
                value={form.density_kg_per_litre}
                onChange={(e) => setVal("density_kg_per_litre", Number(e.target.value))}
                data-testid="assign-density"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Used to convert weight → volume when the driver submits: <span className="font-mono">litres = (loaded − empty) / density</span>
              </div>
            </div>
          )}
          <div className="col-span-2">
            <Label>Notes / instructions</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setVal("notes", e.target.value)}
              placeholder="e.g. purchase order #, gate pass, contact person at source"
              data-testid="assign-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => mut.mutate()}
            disabled={!form.bowser_id || !form.driver_id || !form.vendor_name || mut.isPending}
            data-testid="btn-save-assign"
          >
            <Send size={14} className="mr-1" />
            {mut.isPending ? "Assigning…" : "Assign to driver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RejectDialog({ loading, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/loadings/${loading.id}/reject`, { reason }),
    onSuccess: () => { toast.success("Loading rejected · driver notified to resubmit"); onDone?.(); onClose(); setReason(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Reject failed"),
  });

  return (
    <Dialog open={!!loading} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reject-loading-dialog">
        <DialogHeader>
          <DialogTitle>Reject loading {loading?.code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-status-cancelled/40 bg-status-cancelled/5 p-3 text-xs">
            The driver will get a push notification with your reason and can resubmit the same loading.
          </div>
          <div>
            <Label>Rejection reason <span className="text-status-cancelled">*</span></Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Weight slip photo blurred, density looks off, empty weight inconsistent…"
              data-testid="reject-reason"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => mut.mutate()}
            disabled={!reason.trim() || mut.isPending}
            data-testid="btn-confirm-reject"
          >
            {mut.isPending ? "Rejecting…" : "Reject loading"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DepotLoadingDialog({ open, onClose, onDone }) {
  const { data: tanks } = useQuery({
    queryKey: ["inv-tanks"], queryFn: () => api.get("/inventory/tanks").then((r) => r.data.items || r.data || []),
    enabled: open,
  });
  const { data: bowsers } = useQuery({
    queryKey: ["fleet-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data.items || r.data || []),
    enabled: open,
  });
  const [form, setForm] = useState({
    bowser_id: "", tank_id: "", opening_meter: 0, closing_meter: 0,
    batch_no: "", seal_no: "", density_kg_per_litre: 0.833, temperature_c: 28,
    quality_pass: true, notes: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });
  const setVal = (k, v) => setForm({ ...form, [k]: v });

  const litres = Math.max(0, Number(form.closing_meter) - Number(form.opening_meter));
  const tank = (tanks || []).find((t) => t.id === form.tank_id);
  const bowser = (bowsers || []).find((b) => b.id === form.bowser_id);

  const mut = useMutation({
    mutationFn: () => api.post("/loadings", form),
    onSuccess: () => { toast.success("Loading recorded · stocks updated"); onDone?.(); onClose(); setForm({ ...form, opening_meter: 0, closing_meter: 0 }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="new-loading-dialog">
        <DialogHeader><DialogTitle>Record depot loading</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Source tank</Label>
            <Select value={form.tank_id} onValueChange={(v) => setVal("tank_id", v)}>
              <SelectTrigger data-testid="loading-tank"><SelectValue placeholder="Choose tank" /></SelectTrigger>
              <SelectContent>
                {(tanks || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.code} · {t.name} · {(t.current_stock || 0).toFixed(0)} L</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target bowser</Label>
            <Select value={form.bowser_id} onValueChange={(v) => setVal("bowser_id", v)}>
              <SelectTrigger data-testid="loading-bowser"><SelectValue placeholder="Choose bowser" /></SelectTrigger>
              <SelectContent>
                {(bowsers || []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.code} · {(b.current_stock || 0).toFixed(0)} / {(b.capacity_litres || 0).toFixed(0)} L</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pump opening meter</Label>
            <Input type="number" step="0.01" value={form.opening_meter} onChange={set("opening_meter")} data-testid="loading-opening" />
          </div>
          <div>
            <Label>Pump closing meter</Label>
            <Input type="number" step="0.01" value={form.closing_meter} onChange={set("closing_meter")} data-testid="loading-closing" />
          </div>
          <div className="col-span-2 rounded-md bg-primary/5 border border-primary/30 p-3 text-sm">
            <div className="flex items-center gap-2 font-bold">
              <Fuel size={14} /> Litres transferred: <span className="text-primary text-lg">{litres.toFixed(1)} L</span>
            </div>
            {tank && litres > (tank.current_stock || 0) && (
              <div className="mt-1 text-xs text-status-cancelled">
                ⚠ Tank {tank.code} only has {(tank.current_stock || 0).toFixed(0)} L available.
              </div>
            )}
            {bowser && bowser.capacity_litres && (litres + (bowser.current_stock || 0)) > bowser.capacity_litres && (
              <div className="mt-1 text-xs text-status-cancelled">
                ⚠ Bowser {bowser.code} would overflow (cap {bowser.capacity_litres} L, current {bowser.current_stock || 0} L).
              </div>
            )}
          </div>
          <div>
            <Label>Batch #</Label>
            <Input value={form.batch_no} onChange={set("batch_no")} data-testid="loading-batch" />
          </div>
          <div>
            <Label>Seal #</Label>
            <Input value={form.seal_no} onChange={set("seal_no")} data-testid="loading-seal" />
          </div>
          <div>
            <Label>Density (kg/L)</Label>
            <Input type="number" step="0.001" value={form.density_kg_per_litre} onChange={set("density_kg_per_litre")} />
          </div>
          <div>
            <Label>Temperature (°C)</Label>
            <Input type="number" step="0.1" value={form.temperature_c} onChange={set("temperature_c")} />
          </div>
          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={form.quality_pass} onCheckedChange={(v) => setVal("quality_pass", v)} data-testid="loading-quality" />
            <span className="text-sm">Quality tests passed</span>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} placeholder="e.g. depot invoice ref, tanker origin" />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => mut.mutate()}
            disabled={!form.bowser_id || !form.tank_id || litres <= 0 || mut.isPending}
            data-testid="btn-save-loading"
          >
            <Warehouse size={14} className="mr-1" /> {mut.isPending ? "Saving…" : "Record loading"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
